/**
 * Lyrixa'S OWN COMPLETENESS CHECKS FOR A RELEASE.
 *
 * ============================================================================
 * THESE ARE NOT LABELGRID'S VALIDATION RULES, AND THE DISTINCTION IS THE POINT.
 *
 * §14 says to use `POST /releases/{release}/validate` rather than "creating a
 * fake validation system". That endpoint is the distributor's verdict and it
 * cannot be called from this build (see api/publisherApi.validateRelease, which
 * rejects rather than inventing a pass).
 *
 * What this file does instead is check that the form is COMPLETE — that the user
 * filled in the fields the API's own required-field lists name, and that the
 * release has audio to deliver. That is a different claim, it is one this app can
 * honestly make, and the UI labels it as such: "Lyrixa checks" separate from
 * "Distributor checks", with the second shown as not yet run.
 *
 * The rule for adding anything here: it must be derivable from the committed
 * OpenAPI spec's required-field lists, or from a fact about Lyrixa's own data. No
 * invented artwork dimensions, no invented title-length limits, no invented
 * character rules. §11 is explicit — "Do not invent validation rules" — and a
 * plausible-looking "artwork must be at least 3000×3000" would be exactly that.
 * ============================================================================
 */
import { describePublisherSongBlocker } from '../api/publisherApi';
import { isUploadedSong } from './license';
import type { PublisherRelease, Song } from '../types/domain';

/**
 * One thing standing between a release and submission.
 *
 * `step` routes the user back to the wizard step that can fix it, which is what makes
 * §14's "Action Required" list actionable. `blocking` distinguishes a missing required
 * field from an advisory.
 */
export interface PublisherIssue {
  /** Stable id, for keys and tests. */
  id: string;
  /** The wizard step number (1–8) that fixes this. */
  step: number;
  /** One sentence, addressed to the user. */
  message: string;
  /** True when submission must be prevented. */
  blocking: boolean;
}

/**
 * Every completeness problem with a release, in wizard order.
 *
 * DERIVED FROM THE SPEC'S REQUIRED LISTS, mapped onto the fields this UI collects:
 *
 *   - `titles` is required on a release → a title is required here.
 *   - `artists` is required and non-empty → an artist name is required.
 *   - `content_type` is required → the release type, always set by step 1.
 *   - `audio_language` is required on a track → the language, with `zxx` permitted.
 *   - A release must contain at least one track → at least one song.
 *   - `dsp_configs` carries outlet selection → at least one outlet, or the release
 *     goes nowhere.
 *   - And the one that is about Lyrixa rather than LabelGrid: every song needs audio.
 *
 * NOT CHECKED, because the spec does not require it: `release_date` (optional —
 * LabelGrid schedules when absent), `barcode_number` (auto-generated), `isrc`
 * (assigned), `secondary_genre_id`, lyrics (no required field anywhere), and artwork
 * dimensions (the spec states no constraints, so this app must not either).
 *
 * Artwork IS checked for presence but only as an advisory, because the spec's required
 * list for `POST /releases` includes `artwork_ai_usage` — a declaration ABOUT artwork —
 * while the image itself travels through a separate upload endpoint and is not in any
 * required list.
 */
export function findPublisherIssues(input: {
  release: PublisherRelease;
  songs: Song[];
  /**
   * Whether `songs` is the account's actual library or just what has arrived so far.
   *
   * ============================================================================
   * A SONG THAT HAS NOT LOADED IS NOT A SONG THAT WAS DELETED, and this flag exists
   * because this file could not tell the difference. `songs` is whatever songsStore holds
   * at the moment of the call, which before `GET /songs` answers — or permanently, if it
   * fails — is nothing at all. Every id on the release then resolves to nothing, and the
   * loop below told the user, once per track, that the song was "no longer in your library"
   * and to "remove or replace it". That is a false statement about their data attached to
   * an instruction to destroy the track list, on the screen where they are about to pay a
   * fee. It is the same class of confusion routes/Playlist.tsx's `unresolved` guard exists
   * to prevent, and worse, because that one only shows the wrong empty state.
   *
   * TRUE FOR `idle` AS WELL AS `ready`, which the caller decides: in mock mode no read is
   * ever made, so the fixture in the store IS the library and an unresolvable id there
   * really does mean the song is gone. See hooks/usePublisher.
   *
   * The unknown case still BLOCKS. Not knowing whether the release's audio exists is not
   * permission to submit it — it is a different reason not to, and it gets its own message.
   * ============================================================================
   */
  songsLoaded: boolean;
}): PublisherIssue[] {
  const { release, songs, songsLoaded } = input;
  const issues: PublisherIssue[] = [];

  // ---- step 2: the music
  if (release.songIds.length === 0) {
    issues.push({
      id: 'no-songs',
      step: 2,
      message: 'Choose at least one song from your library.',
      blocking: true,
    });
  }

  /*
    The audio check, per song.

    Blocking, and the most important entry in this list: it is the one that is true of
    every release in this build, and the reason no submission can honestly proceed. The
    message comes from the API boundary rather than being written again here, so the
    explanation the user reads is the same one the refusal uses.
  */
  const unresolvedIds: string[] = [];
  for (const songId of release.songIds) {
    const song = songs.find((s) => s.id === songId);
    if (!song) {
      unresolvedIds.push(songId);
      continue;
    }
    const songBlocker = describePublisherSongBlocker(song);
    if (songBlocker) {
      issues.push({
        id: `${isUploadedSong(song) ? 'license-unverified' : 'no-audio'}-${songId}`,
        step: 2,
        message: songBlocker,
        blocking: true,
      });
    }
  }

  /*
    The ids that resolved to nothing, and WHICH OF THE TWO REASONS applies — see the
    `songsLoaded` doc on the parameter.

    Deleted: one issue per id, as before, keeping the stable `missing-song-<id>` key that
    lets the wizard route the user to the track it is about.

    Not yet known: ONE issue for the whole release rather than one per track, because there
    is only one fact — the library did not arrive — and repeating it per id would read as
    several separate problems with the release. It carries no instruction, because the
    correct action is to wait or retry rather than to edit anything, and it deliberately
    does not name the tracks: nothing here knows whether they are fine.
  */
  if (unresolvedIds.length > 0) {
    if (songsLoaded) {
      for (const songId of unresolvedIds) {
        issues.push({
          id: `missing-song-${songId}`,
          step: 2,
          message: 'A song on this release is no longer in your library. Remove or replace it.',
          blocking: true,
        });
      }
    } else {
      issues.push({
        id: 'songs-unavailable',
        step: 2,
        message:
          'Your library has not loaded, so the tracks on this release could not be checked. ' +
          'Nothing is wrong with the release — reload to try again.',
        blocking: true,
      });
    }
  }

  // ---- step 3: release information. Each of these backs a required API field.
  if (!release.title.trim()) {
    issues.push({
      id: 'no-title',
      step: 3,
      message: 'A release title is required.',
      blocking: true,
    });
  }
  if (!release.artistName.trim()) {
    issues.push({
      id: 'no-artist',
      step: 3,
      message: 'An artist name is required.',
      blocking: true,
    });
  }
  if (release.status === 'draft' && !(release.writerContributor ?? '').trim()) {
    issues.push({
      id: 'no-writer-contributor',
      step: 3,
      message: 'A writer or contributor name is required.',
      blocking: true,
    });
  }
  if (!release.genre.trim()) {
    issues.push({
      id: 'no-genre',
      step: 3,
      message: 'A primary genre is required.',
      blocking: true,
    });
  }
  if (!release.language.trim()) {
    issues.push({
      id: 'no-language',
      step: 3,
      message: 'A language is required. Choose Instrumental if the track has no vocals.',
      blocking: true,
    });
  }

  // ---- step 4: artwork. Advisory — see the note above.
  if (!release.artworkUrl && !release.artworkFromSongId) {
    issues.push({
      id: 'no-artwork',
      step: 4,
      message:
        'No cover art has been chosen. Stores display artwork with every release, so a ' +
        'gradient placeholder is unlikely to be accepted.',
      blocking: false,
    });
  }

  // ---- step 6: outlets
  if (release.outletKeys.length === 0) {
    issues.push({
      id: 'no-outlets',
      step: 6,
      message: 'Choose at least one store or streaming service to deliver to.',
      blocking: true,
    });
  }

  if (release.status === 'draft' && release.rightsAttested !== true) {
    issues.push({
      id: 'rights-attestation-required',
      step: 7,
      message: 'Confirm that you have the rights and permissions required to release every track.',
      blocking: true,
    });
  }

  return issues;
}

/**
 * Whether Lyrixa's own checks pass.
 *
 * Named for what it is — a completeness check, NOT permission to submit. Submission
 * additionally requires LabelGrid validation and verified payment, and those live in
 * api/publisherApi.submitRelease so there is exactly one place the full rule is
 * expressed.
 */
export function isReleaseComplete(input: {
  release: PublisherRelease;
  songs: Song[];
  songsLoaded: boolean;
}): boolean {
  return findPublisherIssues(input).every((issue) => !issue.blocking);
}
